import { useEffect, useMemo, useRef, useState } from 'react';
import OrgModuleLayout from './OrgModuleLayout';
import OrgModulePlaceholder from './OrgModulePlaceholder';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import { useAuthFetch } from '@app/hooks/useAuthFetch';
import { useAuditLogApi } from '@app/hooks/useApis';
import { useApiQuery } from '@app/hooks/useApiQuery';
import { useCursorPagination, type CursorPaginationState } from '@app/hooks/useCursorPagination';
import ListWorkspace, { type ListWorkspaceColumn } from '@app/components/ListWorkspace';
import Drawer from '@app/components/Drawer';
import { formatDateTime } from '@app/utils/format';
import { buildAuditNameLookups, getAuditActorLabel, getAuditResourceLabel, getAuditEventLabel, normalizeAuditEvent } from '@app/utils/audit';
import { useConfig } from '@config/provider';
import { resolveBasePath } from '@app/utils/api';
import type { AuditEventSummary, CursorListResponse, MembershipSummary } from '@app/types/org-console';
import type { AuditEventTypesResponse } from '@app/types/sdk';

const PAGE_SIZE_OPTIONS = [10, 25, 50];
export const AUDIT_SEARCH_DEBOUNCE_MS = 350;

type AuditFilters = { search: string; type: string; fromDate: string; toDate: string };

const formatDateBoundary = (value: string, boundary: 'start' | 'end') => {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) {
    return '';
  }
  const localBoundary = new Date(year, month - 1, day, boundary === 'start' ? 0 : 23, boundary === 'start' ? 0 : 59, boundary === 'start' ? 0 : 59);
  const offsetMinutes = localBoundary.getTimezoneOffset();
  const sign = offsetMinutes <= 0 ? '+' : '-';
  const absoluteOffset = Math.abs(offsetMinutes);
  const offsetHours = String(Math.floor(absoluteOffset / 60)).padStart(2, '0');
  const offsetRemainder = String(absoluteOffset % 60).padStart(2, '0');
  const time = boundary === 'start' ? '00:00:00' : '23:59:59.999999';
  return `${value}T${time}${sign}${offsetHours}:${offsetRemainder}`;
};

export const buildAuditEventsResource = (
  pageSize: number,
  cursor: CursorPaginationState['cursor'],
  filters: AuditFilters
) => {
  const params = new URLSearchParams({ limit: String(pageSize) });
  if (cursor.direction === 'after' && cursor.value) {
    params.set('after', cursor.value);
  } else if (cursor.direction === 'before' && cursor.value) {
    params.set('before', cursor.value);
  }
  if (filters.search.trim()) {
    params.set('q', filters.search.trim());
  }
  if (filters.type.trim()) {
    params.set('type', filters.type.trim());
  }
  if (filters.fromDate) {
    params.set('since', formatDateBoundary(filters.fromDate, 'start'));
  }
  if (filters.toDate) {
    params.set('until', formatDateBoundary(filters.toDate, 'end'));
  }
  return `audit/events?${params.toString()}`;
};

const AuditActivityPage = () => {
  const orgId = useActiveOrgId();
  const { apiBaseUrl } = useConfig();
  const basePath = resolveBasePath(apiBaseUrl);
  const { fetchOrgCollection } = useAuthFetch();
  const auditLogApi = useAuditLogApi();
  const bypassAuditCacheRef = useRef(false);
  const [filters, setFilters] = useState<AuditFilters>({ search: '', type: '', fromDate: '', toDate: '' });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const {
    state: { page, pageSize, cursor },
    setPage,
    setPageSize,
    reset: resetPagination,
    registerPageMeta,
    deriveMeta
  } = useCursorPagination({ initialPageSize: PAGE_SIZE_OPTIONS[0], pageSizeOptions: PAGE_SIZE_OPTIONS });

  const requestFilters = useMemo<AuditFilters>(
    () => ({
      search: debouncedSearch,
      type: filters.type,
      fromDate: filters.fromDate,
      toDate: filters.toDate
    }),
    [debouncedSearch, filters.fromDate, filters.toDate, filters.type]
  );

  const auditFetcher = useMemo(() => {
    if (!orgId) {
      return null;
    }
    const resource = buildAuditEventsResource(pageSize, cursor, requestFilters);
    return () => {
      const bypassCache = bypassAuditCacheRef.current;
      bypassAuditCacheRef.current = false;
      if (bypassCache) {
        return fetchOrgCollection<AuditEventSummary>(basePath, orgId, resource, { cache: 'no-store' });
      }
      return fetchOrgCollection<AuditEventSummary>(basePath, orgId, resource);
    };
  }, [basePath, cursor, fetchOrgCollection, orgId, pageSize, requestFilters]);

  const [refreshVersion, setRefreshVersion] = useState(0);

  const {
    data: events,
    loading,
    error,
    backendMissing
  } = useApiQuery<CursorListResponse<AuditEventSummary>>(auditFetcher, ['audit-events', auditFetcher, refreshVersion]);

  const eventTypesFetcher = useMemo(() => {
    if (!orgId) {
      return null;
    }
    return () => auditLogApi.orgsOrgIdAuditEventTypesGet({ orgId });
  }, [auditLogApi, orgId]);

  const {
    data: eventTypes,
    loading: eventTypesLoading,
    error: eventTypesError,
    backendMissing: eventTypesBackendMissing
  } = useApiQuery<AuditEventTypesResponse>(eventTypesFetcher, ['audit-event-types', eventTypesFetcher]);

  const membershipsFetcher = useMemo(() => {
    if (!orgId) {
      return null;
    }
    return () => fetchOrgCollection<MembershipSummary>(basePath, orgId, 'memberships?limit=500');
  }, [basePath, fetchOrgCollection, orgId]);

  const { data: memberships } = useApiQuery<CursorListResponse<MembershipSummary>>(membershipsFetcher, [
    'audit-memberships',
    membershipsFetcher
  ]);
  const auditNameLookups = useMemo(() => buildAuditNameLookups(memberships?.items ?? []), [memberships]);

  const [activeEvent, setActiveEvent] = useState<AuditEventSummary | null>(null);

  const rows = (events?.items ?? []).map(normalizeAuditEvent);

  useEffect(() => {
    resetPagination();
  }, [orgId, resetPagination]);

  useEffect(() => {
    const normalizedSearch = filters.search.trim();
    if (normalizedSearch === debouncedSearch) {
      return;
    }
    const timer = window.setTimeout(() => {
      setDebouncedSearch(normalizedSearch);
      resetPagination();
    }, AUDIT_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [debouncedSearch, filters.search, resetPagination]);

  useEffect(() => {
    registerPageMeta(events?.meta ?? null);
  }, [events?.meta, registerPageMeta]);

  const paginationMeta = useMemo(() => deriveMeta(rows.length), [deriveMeta, rows.length]);

  const columns: Array<ListWorkspaceColumn<AuditEventSummary>> = [
    { key: 'time', header: 'When', render: (row) => formatDateTime(row.createdAt) },
    { key: 'actor', header: 'Actor', render: (row) => getAuditActorLabel(row.actor, { lookups: auditNameLookups, metadata: row.metadata }) },
    { key: 'type', header: 'Event', render: (row) => getAuditEventLabel(row) },
    { key: 'resource', header: 'Resource', render: (row) => getAuditResourceLabel(row, auditNameLookups) },
    { key: 'ip', header: 'IP', render: (row) => row.ip ?? '—' }
  ];

  const handleFilterReset = () => {
    setFilters({ search: '', type: '', fromDate: '', toDate: '' });
    setDebouncedSearch('');
    resetPagination();
  };
  const filtersActive =
    filters.search.trim().length > 0 || filters.type.trim().length > 0 || filters.fromDate.length > 0 || filters.toDate.length > 0;

  const filterControls = (
    <div className="list-workspace__filter-grid">
      <label>
        Search events
        <input
          type="search"
          placeholder="Actor, resource, event, IP"
          value={filters.search}
          onChange={(event) => {
            setFilters((prev) => ({ ...prev, search: event.target.value }));
          }}
        />
      </label>
      <label>
        Event type
        <select
          value={filters.type}
          onChange={(event) => {
            setFilters((prev) => ({ ...prev, type: event.target.value }));
            resetPagination();
          }}
          disabled={eventTypesLoading || Boolean(eventTypesError) || eventTypesBackendMissing}
        >
          <option value="">All event types</option>
          {(eventTypes?.items ?? []).map((eventType) => (
            <option key={eventType} value={eventType}>
              {eventType}
            </option>
          ))}
        </select>
      </label>
      <label>
        From date
        <input
          type="date"
          value={filters.fromDate}
          onChange={(event) => {
            setFilters((prev) => ({ ...prev, fromDate: event.target.value }));
            resetPagination();
          }}
        />
      </label>
      <label>
        To date
        <input
          type="date"
          value={filters.toDate}
          onChange={(event) => {
            setFilters((prev) => ({ ...prev, toDate: event.target.value }));
            resetPagination();
          }}
        />
      </label>
    </div>
  );

  const handleRowClick = (row: AuditEventSummary) => {
    setActiveEvent(row);
  };

  const drawerOpen = activeEvent != null;
  const closeDrawer = () => {
    setActiveEvent(null);
  };
  const handleRefresh = () => {
    bypassAuditCacheRef.current = true;
    setRefreshVersion((prev) => prev + 1);
  };

  if (!orgId) {
    return (
      <OrgModuleLayout title="Audit & Activity" description="Org-wide audit log with filtering and detail view.">
        <OrgModulePlaceholder intent="Select an organization to load audit events." />
        <OrgModulePlaceholder intent="Audit log tables and JSON drawers render once an organization is active." />
      </OrgModuleLayout>
    );
  }

  if (backendMissing) {
    return (
      <OrgModuleLayout title="Audit & Activity" description="Org-wide audit log with filtering and detail view.">
        <OrgModulePlaceholder intent="Implement this API in the backend to fetch audit events." />
        <OrgModulePlaceholder intent="Implement this API in the backend to display audit JSON details." />
      </OrgModuleLayout>
    );
  }

  const headerStatus = error || eventTypesError || eventTypesBackendMissing ? (
    <div className="list-workspace__status">
      {error ? <div>Failed to load audit events: {error.message}</div> : null}
      {eventTypesError || eventTypesBackendMissing ? <div>Event type filter is temporarily unavailable.</div> : null}
    </div>
  ) : undefined;

  return (
    <OrgModuleLayout title="Audit & Activity" description="Org-wide audit log with filtering and detail view.">
      <ListWorkspace
        rows={rows}
        columns={columns}
        rowId={(row, index) => row.id ?? index}
        paginationMeta={paginationMeta}
        page={page}
        pageSize={pageSize}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        loading={loading}
        emptyState={loading ? <p>Loading audit events…</p> : error ? <p>Unable to load audit events.</p> : <p>No audit activity yet.</p>}
        headerLabel="Audit events"
        filterBar={filterControls}
        onFilterReset={handleFilterReset}
        filterActive={filtersActive}
        onRowClick={handleRowClick}
        highlightRowId={activeEvent?.id ?? null}
        selectionDisabled
        onRefresh={handleRefresh}
        headerStatus={headerStatus}
      />
      <Drawer
        open={drawerOpen}
        title={activeEvent ? getAuditResourceLabel(activeEvent, auditNameLookups) : 'Audit event'}
        subtitle={activeEvent?.resource?.id ?? activeEvent?.actor?.displayName ?? ''}
        onClose={closeDrawer}
      >
        {activeEvent ? (
          <div>
            <div className="record-summary">
              <p className="record-summary__title">Event summary</p>
              <div className="record-summary__grid">
                <div>
                  <span>Type</span>
                  <strong>{getAuditEventLabel(activeEvent)}</strong>
                </div>
                <div>
                  <span>Actor</span>
                  <strong>{getAuditActorLabel(activeEvent.actor, { lookups: auditNameLookups, metadata: activeEvent.metadata })}</strong>
                </div>
                <div>
                  <span>Resource</span>
                  <strong>{getAuditResourceLabel(activeEvent, auditNameLookups)}</strong>
                </div>
                <div>
                  <span>Created</span>
                  <strong>{formatDateTime(activeEvent.createdAt)}</strong>
                </div>
                <div>
                  <span>IP</span>
                  <strong>{activeEvent.ip ?? '—'}</strong>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </Drawer>
    </OrgModuleLayout>
  );
};

export default AuditActivityPage;
